import {
  DropdownContainer,
  DropdownMaxWidth,
  DropdownMenu,
  DropdownOption,
  useDropdownTriggerContainer,
} from '@ardoq/dropdown-2026';
import { Popover2025 } from '@ardoq/popovers';
import { useId, useMemo, useState } from 'react';
import {
  AddLinkIcon,
  DeleteIcon,
  EditIcon,
  IconSize,
  SettingsIcon,
} from '@ardoq/icons';
import { FlexBox, Stack } from '@ardoq/layout';
import { Header4, Text } from '@ardoq/typography';
import { PillButton } from '../PillButton';
import { Separator } from '../Separator';
import { colors } from '@ardoq/design-tokens';
import { PrimaryButton, SecondaryButton } from '@ardoq/button';
import { FormSize, FormWrapper, TextInput } from '@ardoq/forms';
import { DashboardPillLabels, defaultDashboardPillLabels } from './labels';

export type SettingsButtonProps = {
  filterLabel: string;
  removeFilter: {
    onClick: VoidFunction;
  } & ({ isDisabled: false } | { isDisabled: true; extraText: string });
  editLinkedCharts: {
    onClick: VoidFunction;
  } & ({ isDisabled: false } | { isDisabled: true; extraText: string });
  renameFilter: {
    rename: (newFilterName: string) => void;
    validate: (newFilterName: string) => string | undefined;
  } & ({ isDisabled: false } | { isDisabled: true; extraText: string });
  labels?: DashboardPillLabels;
};

const SettingsDropdownMenu = ({
  openRenameFilterDropdown,
  closeDropdown,
  labels = defaultDashboardPillLabels,
  ...props
}: SettingsButtonProps & {
  openRenameFilterDropdown: VoidFunction;
  closeDropdown: VoidFunction;
}) => (
  <DropdownContainer dropdownMaxWidth={DropdownMaxWidth.STANDARD}>
    <DropdownMenu>
      <Stack>
        <DropdownOption
          onClick={props.editLinkedCharts.onClick}
          isDisabled={props.editLinkedCharts.isDisabled}
        >
          <FlexBox flexDirection="column">
            <FlexBox align="center" gap="small">
              <AddLinkIcon iconSize={IconSize.MEDIUM} color={colors.icon} />
              {labels.editLinkedCharts}
            </FlexBox>
            {props.editLinkedCharts.isDisabled && (
              <Text variant="caption">{props.editLinkedCharts.extraText}</Text>
            )}
          </FlexBox>
        </DropdownOption>
        <DropdownOption
          onClick={openRenameFilterDropdown}
          isDisabled={props.renameFilter.isDisabled}
        >
          <FlexBox flexDirection="column">
            <FlexBox align="center" gap="small">
              <EditIcon iconSize={IconSize.MEDIUM} color={colors.icon} />
              {labels.renameFilter}
            </FlexBox>
            {props.renameFilter.isDisabled && (
              <Text variant="caption">{props.renameFilter.extraText}</Text>
            )}
          </FlexBox>
        </DropdownOption>
        <Separator />
        <DropdownOption
          onClick={() => {
            props.removeFilter.onClick();
            closeDropdown();
          }}
          isDisabled={props.removeFilter.isDisabled}
        >
          <FlexBox flexDirection="column">
            <FlexBox align="center" gap="small">
              <DeleteIcon iconSize={IconSize.MEDIUM} color={colors.iconError} />
              <Text color="textError">{labels.deleteFilter}</Text>
            </FlexBox>
            {props.removeFilter.isDisabled && (
              <Text variant="caption" color="textSubtle">
                {props.removeFilter.extraText}
              </Text>
            )}
          </FlexBox>
        </DropdownOption>
      </Stack>
    </DropdownMenu>
  </DropdownContainer>
);

const RenameFilterDropdown = ({
  closeRenameFilterDropdown,
  headingId,
  labels = defaultDashboardPillLabels,
  ...props
}: SettingsButtonProps & {
  closeRenameFilterDropdown: VoidFunction;
  headingId: string;
}) => {
  const [newFilterName, setNewFilterName] = useState<string>(props.filterLabel);
  const trimmedNewFilterName = newFilterName.trim();
  const renamingError = useMemo(
    () => props.renameFilter.validate(trimmedNewFilterName),
    [trimmedNewFilterName, props.renameFilter]
  );

  return (
    <DropdownContainer dropdownMaxWidth={DropdownMaxWidth.NONE}>
      <FormWrapper formSize={FormSize.DEFAULT} gap="medium" padding="medium">
        <Header4 id={headingId}>{labels.renameDialogTitle}</Header4>
        <TextInput
          id="rename-dashboard-filter-input"
          label={labels.filterNameLabel}
          helperText={labels.filterNameHelperText}
          data-autofocus
          onValueChange={setNewFilterName}
          value={newFilterName}
          autoFocus
          hasError={!!renamingError}
          errorMessage={renamingError}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (
                !renamingError &&
                trimmedNewFilterName.length &&
                trimmedNewFilterName !== props.filterLabel
              ) {
                closeRenameFilterDropdown();
                props.renameFilter.rename(trimmedNewFilterName);
              }
            }
          }}
        />
        <FlexBox justify="flex-end" gap="small">
          <SecondaryButton onClick={closeRenameFilterDropdown}>
            {labels.cancelButton}
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              closeRenameFilterDropdown();
              props.renameFilter.rename(trimmedNewFilterName);
            }}
            isDisabled={
              trimmedNewFilterName.length === 0 ||
              trimmedNewFilterName === props.filterLabel ||
              !!renamingError
            }
          >
            {labels.saveButton}
          </PrimaryButton>
        </FlexBox>
      </FormWrapper>
    </DropdownContainer>
  );
};

export const SettingsButton = (props: SettingsButtonProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRenamingFilter, setIsRenamingFilter] = useState(false);
  const optionsMenuTriggerRef = useDropdownTriggerContainer<HTMLButtonElement>({
    isOpen: isMenuOpen,
    openDropdown: () => {
      if (!isMenuOpen) {
        setIsRenamingFilter(false);
        setIsMenuOpen(true);
      }
    },
  });

  const labels = props.labels ?? defaultDashboardPillLabels;
  const popoverId = useId();
  const headingId = `${popoverId}-heading`;
  return (
    <>
      <PillButton
        ref={optionsMenuTriggerRef}
        onClick={() => setIsMenuOpen(prev => !prev)}
        $isActive={isMenuOpen}
        aria-label={labels.settingsAriaLabel(props.filterLabel)}
        aria-haspopup="dialog"
        aria-expanded={isMenuOpen}
        aria-controls={popoverId}
      >
        <SettingsIcon iconSize={IconSize.SMALL} color={colors.icon} />
      </PillButton>
      {!isRenamingFilter && (
        <Popover2025
          id={popoverId}
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onClickOutside={() => setIsMenuOpen(false)}
          anchorRef={optionsMenuTriggerRef}
          placement={{
            side: 'bottom',
            overflow: 'right',
          }}
        >
          <SettingsDropdownMenu
            {...props}
            closeDropdown={() => setIsMenuOpen(false)}
            openRenameFilterDropdown={() => setIsRenamingFilter(true)}
          />
        </Popover2025>
      )}
      {isRenamingFilter && (
        <Popover2025
          role="dialog"
          aria-labelledby={headingId}
          id={popoverId}
          isOpen={isMenuOpen}
          onClose={() => {
            setIsMenuOpen(false);
            setIsRenamingFilter(false);
          }}
          anchorRef={optionsMenuTriggerRef}
          placement={{
            side: 'bottom',
            overflow: 'right',
          }}
          focusContent
        >
          <RenameFilterDropdown
            {...props}
            headingId={headingId}
            closeRenameFilterDropdown={() => {
              setIsRenamingFilter(false);
              setIsMenuOpen(false);
            }}
          />
        </Popover2025>
      )}
    </>
  );
};
